import {
  ToastContainer as _ToastContainer,
  toast as _toast,
  ToastContent,
  ToastOptions,
  cssTransition,
} from 'react-toastify'
import styled, { keyframes } from 'styled-components'
import React from 'react'
import { isServer } from '@/lib/isServer'

export const ToastContainer = styled(_ToastContainer)`
  &.Toastify__toast-container {
    display: flex;
    justify-content: center;
    flex-direction: column;
    .Toastify__toast {
      position: relative;
      top: 120px;
      background: white;
      padding: 20px;
      font-size: 15px;
      border-radius: 15px;
      margin: 20px auto;
      width: fit-content;
      box-shadow: rgba(36, 43, 63, 0.24) 0 20px 70px 0;
      box-sizing: border-box;
      .Toastify__toast-body {
        color: #141c30;
        font-weight: 500;
      }
      svg {
        display: none;
      }
    }
  }
`

const ToastBodyWrapper = styled.div<{ withContent: boolean }>`
  .icon {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    margin-right: 10px;
    margin-top: 2px;
  }
  .aac-toast-title {
    color: #141c30;
    font-weight: 600;
    display: flex;
    ${({ withContent }) => (withContent ? 'margin-bottom: 15px;' : null)}
  }
  .aac-toast-content {
    color: #141c30;
    font-weight: 500;
    display: flex;
  }
`

export const fade = cssTransition({
  enter: 'fade-in',
  exit: 'fade-out',
  appendPosition: false,
  collapse: true,
  collapseDuration: 200,
})

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

const CustomToastBodyWrapper = styled.div`
  background: #101010;
  color: #fff;
  font-size: 14px;
  text-align: center;
  padding: 8px;
  white-space: pre-line;
`

const rotate = keyframes`
from {
  transform: rotate(0deg);
}
to {
  transform: rotate(359deg);
}
`

const LoadingIcon = styled.div`
  border: 3px solid rgba(2, 122, 255, 0.1);
  border-top-color: rgba(2, 122, 255, 1);
  border-radius: 100%;
  min-height: 12px;
  min-width: 12px;
  max-height: 12px;
  max-width: 12px;
  height: 12px;
  width: 12px;
  animation: ${rotate} 0.8s infinite linear;
`

const ToastBody: React.FC<{ icon?: React.ReactNode; title?: ToastContent; content?: ToastContent }> = ({
  icon,
  title,
  content,
}) => {
  return (
    <ToastBodyWrapper withContent={!!content}>
      {!!title && (
        <div className="aac-toast-title">
          {icon && <div className="icon">{icon}</div>}
          <div className="text">{title}</div>
        </div>
      )}
      {!!content && (
        <div className={`aac-toast-content${!title ? ' with-icon' : ''}`}>
          {!title && <div className="icon">{icon}</div>}
          <div className="text">{content}</div>
        </div>
      )}
    </ToastBodyWrapper>
  )
}

const CustomToastBody: React.FC<{ content: ToastContent }> = ({ content }) => {
  return (
    <CustomToastBodyWrapper>
      <div className="text">{content}</div>
    </CustomToastBodyWrapper>
  )
}

interface IToastParams extends ToastOptions {
  content?: ToastContent
  title?: ToastContent
}

/**
 * Usage example:
 * const info = toast.info({ content: 'info', title: 'title' });
 * await doSomething();
 * info.dismiss(); // OR toast.dismiss(info.id)
 */
export const toast = {
  info: ({ content, title, ...options }: IToastParams) => {
    const id = _toast.info(<ToastBody title={title} content={content}></ToastBody>, options)
    return { dismiss: () => _toast.isActive(id) && _toast.dismiss(id), id }
  },
  success: ({ content, title, ...options }: IToastParams) => {
    const id = _toast.success(<ToastBody title={title} content={content}></ToastBody>, options)
    return { dismiss: () => _toast.isActive(id) && _toast.dismiss(id), id }
  },
  error: ({ content, title, ...options }: IToastParams) => {
    const id = _toast.success(<ToastBody title={title} content={content}></ToastBody>, options)
    return { dismiss: () => _toast.isActive(id) && _toast.dismiss(id), id }
  },
  loading: ({ content, title, ...options }: IToastParams) => {
    const id = _toast.success(<ToastBody icon={<LoadingIcon />} title={title} content={content}></ToastBody>, options)
    return { dismiss: () => _toast.isActive(id) && _toast.dismiss(id), id }
  },
  dismiss: (id: string) => _toast.dismiss(id),
  customInfo: ({ content }: IToastParams) => {
    const id = _toast(<CustomToastBody content={content}></CustomToastBody>, {
      transition: fade,
      position: 'bottom-center',
      draggable: false,
      toastId: 'custom-info',
      style: {
        padding: 0,
        bottom: '138px',
        left: '50%',
        transform: 'translateX(-50%)',
        minHeight: '38px',
        background: '#101010',
        borderRadius: '6px',
        minWidth: '235px',
        maxWidth: 'fit-content',
      },
    })
    return { dismiss: () => _toast.isActive(id) && _toast.dismiss(id), id }
  },
}

if (!isServer) {
  ;(window as any).toast = toast
}
